// src/pages/admin/AdminTeam.tsx
import { useState } from "react";
import {
  useGetTeamQuery,
  useAddTeamMemberMutation,
  useUpdateTeamMemberRoleMutation,
  useRemoveTeamMemberMutation,
  useResetTeamMemberPasswordMutation,
} from "../../app/api/adminAuthApi";
import { useSelector } from "react-redux";
import type { RootState } from "../../app/store";
import type { AdminUser, AdminRole } from "../../app/api/adminAuthApi";
import {
  PageHeader,
  Skeleton,
  EmptyState,
  Button,
  Avatar,
  formatDate,
} from "./AdminShared";

import { Check, Users } from "lucide-react";
const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  staff: "Staff",
  tutor: "Tutor",
};
const ROLE_COLORS: Record<AdminRole, { bg: string; text: string }> = {
  super_admin: { bg: "#ebeff7", text: "#4a44b0" },
  admin: { bg: "#edfaf4", text: "#2a7a56" },
  staff: { bg: "#fff8ee", text: "#7a4a10" },
  tutor: { bg: "#f0f7ff", text: "#1a4a7a" },
};

/* ─── Add team member form ───────────────────────────────────────── */
function AddMemberForm({ onDone }: { onDone: () => void }) {
  const [addMember, { isLoading }] = useAddTeamMemberMutation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "staff" | "tutor">("staff");
  const [error, setError] = useState("");
  const [tempPwd, setTempPwd] = useState("");

  const handleSubmit = async () => {
    if (!firstName || !lastName || !email) {
      setError("All fields are required");
      return;
    }
    setError("");
    try {
      const res = await addMember({
        firstName,
        lastName,
        email,
        role,
      }).unwrap();
      setTempPwd(res.temporaryPassword);
    } catch (e: any) {
      setError(e?.data?.error ?? "Failed to add member");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]";

  if (tempPwd)
    return (
      <div className="rounded-[22px] border border-[#b6e8d0] bg-[#edfaf4] p-6 space-y-3">
        <p className="text-base font-bold text-[#2a7a56]">
          <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Team member added
        </p>
        <p className="text-sm text-[#2a7a56]">
          Share this temporary password with them — they should change it on
          first login.
        </p>
        <div className="rounded-[14px] border border-[#b6e8d0] bg-white px-4 py-3 flex items-center justify-between gap-3">
          <p className="font-mono text-lg font-bold tracking-widest text-[#16213e]">
            {tempPwd}
          </p>
          <button
            onClick={() => navigator.clipboard.writeText(tempPwd)}
            className="text-xs font-semibold text-[#1b2d5e] hover:text-[#4a44b0]"
          >
            Copy
          </button>
        </div>
        <Button variant="primary" onClick={onDone}>
          Done
        </Button>
      </div>
    );

  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6 space-y-4">
      <h3 className="text-base font-bold text-[#16213e]">Add team member</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            First name
          </label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Adeola"
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Last name
          </label>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Okonkwo"
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="adeola@futuresmart.ng"
            className={inputCls + " mt-1.5"}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Role
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as "admin" | "staff" | "tutor")}
            className={inputCls + " mt-1.5"}
          >
            <option value="staff">
              Staff — view only, mark attendance and reports
            </option>
            <option value="tutor">
              Tutor — reviews children's work and gives feedback only
            </option>
            <option value="admin">
              Admin — full access except team management
            </option>
          </select>
        </div>
      </div>
      {error && <p className="text-xs text-[#e57373]">{error}</p>}
      <div className="flex gap-3">
        <Button variant="secondary" className="flex-1" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="primary"
          className="flex-1"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? "Adding…" : "Add member"}
        </Button>
      </div>
    </div>
  );
}

/* ─── Team member card ───────────────────────────────────────────── */
function MemberCard({
  member,
  isSelf,
  canManage,
}: {
  member: AdminUser;
  isSelf: boolean;
  canManage: boolean;
}) {
  const [updateRole, { isLoading: updatingRole }] =
    useUpdateTeamMemberRoleMutation();
  const [remove, { isLoading: removing }] = useRemoveTeamMemberMutation();
  const [resetPwd, { isLoading: resetting }] =
    useResetTeamMemberPasswordMutation();

  const [confirmRemove, setConfirmRemove] = useState(false);
  const [tempPwd, setTempPwd] = useState("");
  const [err, setErr] = useState("");

  const rc = ROLE_COLORS[member.role];

  const handleRoleChange = async (newRole: "admin" | "staff" | "tutor") => {
    try {
      await updateRole({ id: member.id, role: newRole }).unwrap();
    } catch (e: any) {
      setErr(e?.data?.error ?? "Failed");
    }
  };

  const handleReset = async () => {
    try {
      const res = await resetPwd(member.id).unwrap();
      setTempPwd(res.temporaryPassword);
    } catch (e: any) {
      setErr(e?.data?.error ?? "Failed");
    }
  };

  const handleRemove = async () => {
    try {
      await remove(member.id).unwrap();
      setConfirmRemove(false);
    } catch (e: any) {
      setErr(e?.data?.error ?? "Failed");
    }
  };

  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-5 space-y-4">
      <div className="flex items-start gap-3">
        <Avatar
          firstName={member.firstName}
          lastName={member.lastName}
          size="md"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-[#16213e]">
              {member.firstName} {member.lastName}
            </p>
            {isSelf && (
              <span className="rounded-full bg-[#ebeff7] px-2 py-0.5 text-[11px] font-bold text-[#1b2d5e]">
                You
              </span>
            )}
          </div>
          <p className="text-xs text-[#8189a0] truncate">{member.email}</p>
          <p className="text-[11.5px] text-[#a6adbf] mt-0.5">
            {member.lastLoginAt
              ? `Last login ${formatDate(member.lastLoginAt)}`
              : "Never logged in"}
          </p>
        </div>
        <span
          className="rounded-full px-2.5 py-0.5 text-[12.5px] font-bold flex-shrink-0"
          style={{ background: rc.bg, color: rc.text }}
        >
          {ROLE_LABELS[member.role]}
        </span>
      </div>

      {tempPwd && (
        <div className="rounded-[14px] border border-[#b6e8d0] bg-[#edfaf4] px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-[11.5px] font-bold text-[#2a7a56] mb-0.5">
              Temporary password
            </p>
            <p className="font-mono text-base font-bold tracking-widest text-[#16213e]">
              {tempPwd}
            </p>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(tempPwd);
              setTempPwd("");
            }}
            className="text-xs font-semibold text-[#1b2d5e]"
          >
            Copy & close
          </button>
        </div>
      )}

      {err && <p className="text-xs text-[#e57373]">{err}</p>}

      {canManage && !isSelf && member.role !== "super_admin" && (
        <div className="flex flex-wrap gap-2 pt-1 border-t border-[#f7f8fb]">
          {/* Role toggle */}
          {member.role === "staff" ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleRoleChange("admin")}
              disabled={updatingRole}
            >
              {updatingRole ? "…" : "Promote to Admin"}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleRoleChange("staff")}
              disabled={updatingRole}
            >
              {updatingRole ? "…" : "Demote to Staff"}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleReset}
            disabled={resetting}
          >
            {resetting ? "…" : "Reset password"}
          </Button>
          {!confirmRemove ? (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setConfirmRemove(true)}
            >
              Remove
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#e57373] font-semibold">
                Sure?
              </span>
              <Button
                variant="danger"
                size="sm"
                onClick={handleRemove}
                disabled={removing}
              >
                {removing ? "…" : "Yes, remove"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmRemove(false)}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════ */
export default function AdminTeam() {
  const { data, isLoading } = useGetTeamQuery();
  const me = useSelector((s: RootState) => s.adminAuth.user);
  const isSuperAdmin = me?.role === "super_admin";
  const [showForm, setShowForm] = useState(false);

  const members = data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Team"
        subtitle="Manage admin and staff accounts for Future Smart Lab."
      />

      <div className="mb-5 flex items-center justify-between">
        <p className="text-xs text-[#8189a0]">
          {members.length} team member{members.length !== 1 ? "s" : ""}
        </p>
        {isSuperAdmin && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? "Cancel" : "+ Add member"}
          </Button>
        )}
      </div>

      {showForm && (
        <div className="mb-5">
          <AddMemberForm onDone={() => setShowForm(false)} />
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-[22px]" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No team members yet"
          subtitle="Add admins and staff to help manage the academy."
          action={
            isSuperAdmin ? (
              <Button variant="primary" onClick={() => setShowForm(true)}>
                Add first member
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {members.map((m: AdminUser) => (
            <MemberCard
              key={m.id}
              member={m}
              isSelf={m.id === me?.id}
              canManage={isSuperAdmin}
            />
          ))}
        </div>
      )}
    </>
  );
}
